import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Divider, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Drawer } from "@zfp-akademie-a-s/zfp-design";
import { FormCheckbox, FormSelect, FormTextField } from "@zfp-akademie-a-s/zfp-design/form";
import { FormNumberField } from "@zfp-akademie-a-s/zfp-design/form/number-field";
import { type MovableAssetFormType, getMovableAssetFormSchema } from "Forms/Schemas/Assets/movableAssetFormSchema";
import { type Option, toSelectOptions } from "Forms/fieldOptions";
import {
  getGetAssetsOverviewQueryKey,
  getListMovableAssetItemsQueryKey,
  useCreateMovableAssetItem,
  useDeleteAssetItem,
  useUpdateMovableAssetItem,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import { AmountFrequency, type MovableAssetItem, MovableAssetItemMovableSubType } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { MOVABLE_SUBTYPE_RESOURCE_KEY } from "Infrastructure/Utils/AssetUtils";
import { type FC, useContext, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "sonner";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";
import { NOTE_MAX_LENGTH } from "../constants";

type Props = {
  readonly onClose: () => void;
  readonly movableAsset?: MovableAssetItem;
  readonly open: boolean;
};

export const MovableAssetDrawer: FC<Props> = ({ onClose, movableAsset, open }) => {
  const { readOnly, analysisId, versionId } = useContext(AnalysisDetailContext);

  const { t } = useResource();
  const queryClient = useQueryClient();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: getGetAssetsOverviewQueryKey(analysisId, versionId) });
    queryClient.invalidateQueries({ queryKey: getListMovableAssetItemsQueryKey(analysisId, versionId) });
  };

  const { mutate: create } = useCreateMovableAssetItem();
  const { mutate: update } = useUpdateMovableAssetItem();
  const { mutate: deleteItem } = useDeleteAssetItem();

  const movableAssets: Option[] = (Object.keys(MovableAssetItemMovableSubType) as Array<MovableAssetItemMovableSubType>).map((value) => ({
    value,
    name: t(Resources.AnalysisDetail.Assets.Movable.MovableAssetType[MOVABLE_SUBTYPE_RESOURCE_KEY[value]]),
  }));

  const methods = useForm<MovableAssetFormType>({
    resolver: yupResolver(getMovableAssetFormSchema(t)),
    mode: "onBlur",
  });

  const { control, handleSubmit, reset, watch, setValue } = methods;

  useEffect(() => {
    if (open) {
      const defaultValues = {
        movableAssetType: movableAsset?.movableSubType,
        name: movableAsset?.name,
        value: movableAsset?.amount.value,
        isCoveredByInsurance: movableAsset?.isCoveredByInsurance,
        isUnderFinancing: movableAsset?.isUnderFinancing,
        isUsableForGoals: movableAsset?.isUsableForGoals,
        note: movableAsset?.note ?? undefined,
      };
      reset(defaultValues);
    }
  }, [movableAsset, open, reset]);

  const onSubmit = (data: MovableAssetFormType) => {
    try {
      const payload = {
        amount: {
          currencyCode: "CZK",
          frequency: AmountFrequency.one_time,
          value: data.value,
        },
        note: data.note || "",
        name: data.name,
        movableSubType: data.movableAssetType as MovableAssetItemMovableSubType,
        isCoveredByInsurance: !!data.isCoveredByInsurance,
        isUnderFinancing: !!data.isUnderFinancing,
        isUsableForGoals: !!data.isUsableForGoals,
      };

      if (!movableAsset) {
        create(
          { analysisId, versionId, data: payload },
          {
            onSuccess: () => {
              toast.success(t(Resources.AnalysisDetail.Assets.Movable.Created));
              invalidate();
              onClose();
            },
            onError: () => toast.error(t(Resources.AnalysisDetail.Assets.Movable.Error)),
          },
        );
      } else {
        update(
          { analysisId, versionId, itemId: movableAsset.guid, data: payload },
          {
            onSuccess: () => {
              toast.success(t(Resources.AnalysisDetail.Assets.Movable.Edited));
              invalidate();
              onClose();
            },
            onError: () => toast.error(t(Resources.AnalysisDetail.Assets.Movable.Error)),
          },
        );
      }
    } catch {
      toast.error(t(Resources.AnalysisDetail.Assets.Movable.Error));
    }
  };

  const onDelete = () => {
    try {
      deleteItem(
        { analysisId, versionId, itemId: movableAsset?.guid || "" },
        {
          onSuccess: () => {
            toast.success(t(Resources.AnalysisDetail.Assets.Movable.Deleted));
            invalidate();
            onClose();
          },
          onError: () => toast.error(t(Resources.AnalysisDetail.Assets.Movable.Error)),
        },
      );
    } catch {
      toast.error(t(Resources.AnalysisDetail.Assets.Movable.Error));
    }
  };

  const movableAssetName = watch("name");

  const onChangeType = (value: string) => {
    if (movableAssetName && movableAssetName.length > 0) return;
    const preFilledName = movableAssets.find((ma) => ma.value === value)?.name;
    setValue("name", preFilledName ?? "");
  };

  return (
    <FormProvider {...methods}>
      <Drawer
        open={open}
        onClose={onClose}
        closeLabel={t(Resources.Common.Close)}
        title={movableAsset ? t(Resources.AnalysisDetail.Assets.Movable.Edit) : t(Resources.AnalysisDetail.Assets.Movable.Add)}
        onSubmit={handleSubmit(onSubmit)}
        startAction={
          !!movableAsset && (
            <Button variant="text" color="error" disabled={readOnly} onClick={onDelete}>
              {t(Resources.Common.Delete)}
            </Button>
          )
        }
        primaryAction={
          <Button variant="contained" type="submit" disabled={readOnly}>
            {movableAsset ? t(Resources.Common.Edit) : t(Resources.Common.Create)}
          </Button>
        }
      >
        <Stack spacing={Spacing.XL} py={Spacing.XXL}>
          <FormSelect
            control={control}
            name="movableAssetType"
            label={t(Resources.AnalysisDetail.Assets.Movable.MovableAssetType.Title)}
            required
            options={toSelectOptions(movableAssets)}
            placeholder={t(Resources.Common.SelectPlaceholder)}
            disabled={readOnly}
            onChange={onChangeType}
            fullWidth
          />
          <FormTextField
            control={control}
            name="name"
            label={t(Resources.AnalysisDetail.Assets.Name)}
            required
            disabled={readOnly}
            fullWidth
          />
          <FormNumberField
            control={control}
            name="value"
            label={t(Resources.AnalysisDetail.Assets.Value)}
            required
            scale={0}
            min={0}
            thousandsSeparator=" "
            unit={t(Resources.Common.CZKSuffix)}
            disabled={readOnly}
            placeholder="0"
            fullWidth
          />
          <FormTextField
            control={control}
            name="note"
            label={t(Resources.AnalysisDetail.Goals.GoalNote)}
            maxLength={NOTE_MAX_LENGTH}
            disabled={readOnly}
            multiline
            minRows={6}
            maxRows={8}
            fullWidth
          />
          <Divider />
          <Typography fontWeight={600} variant="subtitle1">
            {t(Resources.AnalysisDetail.Assets.Attributes)}
          </Typography>
          <Stack spacing={Spacing.S}>
            <FormCheckbox
              control={control}
              name="isUsableForGoals"
              label={t(Resources.AnalysisDetail.Assets.IsUsableForGoals)}
              disabled={readOnly}
            />
            <FormCheckbox
              control={control}
              name="isCoveredByInsurance"
              label={t(Resources.AnalysisDetail.Assets.IsCoveredByInsurance)}
              disabled={readOnly}
            />
            <FormCheckbox
              control={control}
              name="isUnderFinancing"
              label={t(Resources.AnalysisDetail.Assets.IsUnderFinancing)}
              disabled={readOnly}
            />
          </Stack>
        </Stack>
      </Drawer>
    </FormProvider>
  );
};
